// web/src/copilot/CopilotProvider.tsx
//
// Wraps the app with CopilotKit, pointing at the CopilotKit Runtime service.
// In dev, the Runtime runs locally on port 8001. In prod, set
// VITE_COPILOTKIT_RUNTIME_URL=https://runtime.tapelens.com in Cloudflare Pages.
// The Supabase JWT is forwarded on every request via useSupabaseAuthHeaders,
// which keeps the token fresh via Supabase auto-refresh + a 5-min defensive
// interval (avoids the "Signature has expired" 401s on long-lived tabs).
//
// 429 / rate-limit handling:
// CopilotKit's onError prop only fires when publicApiKey is set (Copilot Cloud
// feature). Since we use a self-hosted runtime, we intercept window.fetch
// instead. The interceptor is installed only while this provider is mounted
// and only inspects requests whose URL includes the copilotkit runtime path,
// so it doesn't affect unrelated fetch calls. A ref keeps the current tier
// fresh inside the long-lived closure without reinstalling the interceptor.
import { CopilotKit } from "@copilotkit/react-core";
import { useEffect, useRef, type ReactNode } from "react";
import { toast } from "sonner";
import { useSupabaseAuthHeaders } from "./useSupabaseAuthHeaders";
import { useUsage } from "./api";
import { parseQuotaError } from "./useQuotaErrorHandler";
import { QuotaExceededMessage } from "./QuotaExceededMessage";

export function CopilotProvider({ children }: { children: ReactNode }) {
  const runtimeBase =
    import.meta.env.VITE_COPILOTKIT_RUNTIME_URL || "http://localhost:8001";
  const getHeaders = useSupabaseAuthHeaders();
  const { data: usage } = useUsage();

  // Keep current tier accessible inside the stable fetch interceptor closure.
  const tierRef = useRef(usage?.tier);
  useEffect(() => {
    tierRef.current = usage?.tier;
  }, [usage?.tier]);

  // Intercept fetch to catch 429s from the copilotkit runtime endpoint.
  // We can't use CopilotKit's onError prop because it only fires when
  // publicApiKey is provided (Copilot Cloud only).
  useEffect(() => {
    const runtimePath = `${runtimeBase}/copilotkit`;
    const originalFetch = window.fetch;

    window.fetch = async function interceptedFetch(
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;

      const response = await originalFetch(input, init);

      // Only inspect responses to the copilotkit runtime
      if (!url.includes(runtimePath)) return response;
      // 429 = rate-limit, 402 = tier paywall (free user reached a Pro endpoint)
      if (response.status !== 429 && response.status !== 402) return response;

      // Clone so the body stream is still readable by CopilotKit
      const cloned = response.clone();
      cloned
        .json()
        .then((body: unknown) => {
          const quota = parseQuotaError({ status: response.status, body });
          if (quota) {
            toast(<QuotaExceededMessage kind={quota.kind} tier={tierRef.current} />, {
              duration: 8000,
            });
          }
        })
        .catch(() => {
          // Malformed body — still surface a generic quota message
          toast(
            <QuotaExceededMessage kind="service_unavailable" tier={tierRef.current} />,
            { duration: 8000 },
          );
        });

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [runtimeBase]);

  return (
    <CopilotKit runtimeUrl={`${runtimeBase}/copilotkit`} headers={getHeaders}>
      {children}
    </CopilotKit>
  );
}
